import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState } from "../components/shared";
import { formatNum, formatMoney, can } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Undo2, Factory, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Production() {
  const { user } = useAuth();
  const editable = can(user.role, "production");
  const base = user.organization?.base_currency || "NGN";
  const [runs, setRuns] = useState([]);
  const [products, setProducts] = useState([]);
  const [raws, setRaws] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ product_id: "", warehouse_id: "", quantity: 1 });

  const load = useCallback(async () => {
    const [r, p, rm, w] = await Promise.all([
      api.get("/production"), api.get("/products"), api.get("/raw-materials"), api.get("/warehouses"),
    ]);
    setRuns(r.data); setProducts(p.data); setRaws(rm.data); setWarehouses(w.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const product = products.find((p) => p.id === form.product_id);
  const whName = (id) => warehouses.find((w) => w.id === id)?.name || "—";
  const prodName = (id) => products.find((p) => p.id === id)?.name || "—";

  const rawInWh = (rid) => raws.find((r) => r.id === rid && r.warehouse_id === form.warehouse_id);

  const openNew = () => { setForm({ product_id: products[0]?.id || "", warehouse_id: warehouses[0]?.id || "", quantity: 1 }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/production", form); toast.success("Production recorded — raw materials deducted"); setOpen(false); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const reverse = async (id) => { if (!window.confirm("Reverse this production run? Raw materials will be restored.")) return; try { await api.delete(`/production/${id}`); toast.success("Reversed"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };

  return (
    <div data-testid="production-page">
      <PageHeader title="Production" subtitle="Produce finished goods — consumes raw materials via BoM"
        action={editable && <Button className="rounded-none gap-2" data-testid="create-production-btn" onClick={openNew} disabled={products.length === 0}><Plus className="w-4 h-4" /> New Production Run</Button>} />

      {runs.length === 0 ? (
        <EmptyState title="No production yet" hint="Record a production run to convert raw materials into finished-product stock. You need at least one product defined." />
      ) : (
        <div className="border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-secondary">
              <TableHead>Date</TableHead><TableHead>Product</TableHead><TableHead>Warehouse</TableHead>
              <TableHead className="text-right">Units</TableHead>
              <TableHead className="text-right">Material Cost</TableHead><TableHead className="text-right">Unit Cost</TableHead>
              <TableHead>By</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {runs.map((r) => (
                <TableRow key={r.id} className="hover:bg-secondary/60" data-testid="production-row">
                  <TableCell className="font-mono text-sm">{(r.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell className="font-medium">{r.product_name || prodName(r.product_id)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{whName(r.warehouse_id)}</TableCell>
                  <TableCell className="text-right font-mono font-bold">{formatNum(r.quantity, 0)}</TableCell>
                  <TableCell className="text-right font-mono" data-testid="production-material-cost">{r.material_cost != null ? formatMoney(r.material_cost, base) : "—"}</TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">{r.unit_cost != null ? formatMoney(r.unit_cost, base) : "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.created_by}</TableCell>
                  <TableCell className="text-right">
                    {editable && <Button size="sm" variant="ghost" className="rounded-none text-destructive gap-1" onClick={() => reverse(r.id)} data-testid="reverse-production-btn"><Undo2 className="w-3.5 h-3.5" /> Reverse</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Factory className="w-5 h-5" /> New Production Run</DialogTitle><DialogDescription>Produce finished goods; raw materials are consumed per the bill of materials.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label>Product</Label>
                <Select value={form.product_id} onValueChange={(v) => setForm((f) => ({ ...f, product_id: v }))}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="production-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                  <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Warehouse</Label>
                <Select value={form.warehouse_id} onValueChange={(v) => setForm((f) => ({ ...f, warehouse_id: v }))}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="production-warehouse-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Quantity to produce</Label><Input type="number" step="any" min="0.01" className="rounded-none mt-1" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: parseFloat(e.target.value || 0) }))} data-testid="production-qty-input" /></div>
            </div>

            {product && (
              <div className="border border-border p-3 bg-secondary/40">
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Raw material requirement</div>
                {product.composition.length === 0 ? (
                  <p className="text-xs text-destructive">This product has no composition defined.</p>
                ) : (
                  <div className="space-y-1.5">
                    {product.composition.map((c, i) => {
                      const rm = rawInWh(c.raw_material_id);
                      const need = c.quantity * (form.quantity || 0);
                      const have = rm?.quantity ?? 0;
                      const ok = rm && have >= need;
                      return (
                        <div key={`${c.raw_material_id}-${i}`} className="flex items-center justify-between text-xs font-mono">
                          <span className="flex items-center gap-1">
                            {ok ? <CheckCircle2 className="w-3.5 h-3.5 text-success" /> : <AlertTriangle className="w-3.5 h-3.5 text-destructive" />}
                            {raws.find((r) => r.id === c.raw_material_id)?.name || "Unknown"}
                          </span>
                          <span className={ok ? "" : "text-destructive"}>need {formatNum(need)} / have {formatNum(have)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <DialogFooter><Button type="submit" className="rounded-none" data-testid="save-production-btn">Produce</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
